import React, { useMemo, useEffect, useRef, useState } from 'react';

import { Row, Col, notification } from 'antd';
import { Formik } from 'formik';

import styled from 'styled-components';

import { getContactGender } from '@libs/NwFeatures';

import { useEditFeatures } from '@hooks/API/Contact/UseContactFeatures';
import { useFeaturesIndex, useGlobalFeatures, useGlobalConversions } from '@hooks/API/UseFeatures';

import {
    createFeatureItemForm,
    getActivePublicFieldIDs,
    getFeatureInitialValues,
    getFeatureFormColSpan,
    getPublicValueFieldID,
    getPublicValueItem,
    getRealValueFieldID,
    getVisibleGroupFeatures,
    hasFeatureValue,
} from '@components/Features/FeatureItemFormUtils';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FormContainer = styled.div`
    padding: 0;
`;

const Fieldset = styled.fieldset`
    margin: 0 0 1rem 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    border: 0;
    min-width: 0;
`;

const Label = styled.label`
    margin: 0;
    flex: 1;
    min-width: 0;
    font-weight: normal;
    text-transform: uppercase;
    font-size: 0.85rem;
    color: ${(props) => (props.$isActive ? props.theme.TextColor : props.theme.TextColorMuted)};
    display: inline-flex;
    align-items: center;
    width: auto;

    &::before {
        content: '';
        display: ${(props) => (props.$isActive ? 'inline-block' : 'none')};
        width: 0.45rem;
        height: 0.45rem;
        margin-right: 0.4rem;
        border-radius: 50%;
        background-color: ${(props) => props.theme.WarningColor};
    }
`;

const AreaName = styled.div`
    font-size: ${(props) => props.theme.FontSizeLg};
    text-transform: capitalize;
    margin-bottom: 1.5rem;
    font-weight: bold;
    color: ${(props) => props.theme.TextColor};
`;

const FeatureLabelHeader = styled.div`
    padding: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .ant-btn {
        flex: 0 0 auto;
        height: auto;
        padding: 0;
        font-size: 0.85rem;
        line-height: 1;
    }

    .ant-btn svg {
        margin-right: 0.35rem;
    }
`;

const FeatureFieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EditFeatureDrawer = ({
    featuresGroup,
    contactFeatures,
    contact,
    contactType,
    editFeatureName,
    updatedFieldIDs,
    setUpdatedFieldIDs,
    onClose,
}) => {
    const initialValues = getFeatureInitialValues(featuresGroup.GroupFeatures, contactFeatures);
    const featuresIndex = useFeaturesIndex();
    const { data: globalFeatures } = useGlobalFeatures();
    const { data: globalConversions } = useGlobalConversions();
    const { mutateAsync: editFeatures } = useEditFeatures();
    const [updatingFieldID, setUpdatingFieldID] = useState(null);
    const updatingFieldIDRef = useRef(null);
    const clearingPublicFieldIDsRef = useRef([]);
    const [activePublicFieldIDs, setActivePublicFieldIDs] = useState(() =>
        getActivePublicFieldIDs(featuresGroup.GroupFeatures, contactFeatures)
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            const element = document.getElementById('active-feature');
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const publicFieldIDs = getActivePublicFieldIDs(featuresGroup.GroupFeatures, contactFeatures);
        if (!publicFieldIDs.length) {
            return;
        }
        setActivePublicFieldIDs((currentFieldIDs) => {
            const nextFieldIDs = [...currentFieldIDs];
            publicFieldIDs.forEach((fieldID) => {
                if (!nextFieldIDs.find((currentFieldID) => currentFieldID === fieldID)) {
                    nextFieldIDs.push(fieldID);
                }
            });
            return nextFieldIDs.length === currentFieldIDs.length ? currentFieldIDs : nextFieldIDs;
        });
    }, [featuresGroup.GroupFeatures, contactFeatures]);

    // forma funzionale: due campi modificati a breve distanza leggerebbero la stessa lista e uno dei due pallini
    // non comparirebbe
    const handleUpdatedFieldIDs = (updatingFieldName) => {
        const updatedFeatureID = getRealValueFieldID(updatingFieldName);
        setUpdatedFieldIDs((currentFieldIDs) =>
            currentFieldIDs.find((fieldName) => fieldName === updatedFeatureID)
                ? currentFieldIDs
                : [...currentFieldIDs, updatedFeatureID]
        );
    };

    const handleCurrentUpdatingFieldID = (fieldID) => {
        updatingFieldIDRef.current = fieldID;
        setUpdatingFieldID(fieldID);
    };

    const handleActivatePublicValue = async (fieldID, privateValue, setFieldValue) => {
        const publicValueFieldID = getPublicValueFieldID(fieldID);
        await setFieldValue(publicValueFieldID, privateValue);
        setActivePublicFieldIDs((currentFieldIDs) => {
            if (currentFieldIDs.find((currentFieldID) => currentFieldID === publicValueFieldID)) {
                return currentFieldIDs;
            }
            return [...currentFieldIDs, publicValueFieldID];
        });
    };

    const handleClearPublicValue = async (fieldID, setFieldValue, handleSubmit) => {
        const publicValueFieldID = getPublicValueFieldID(fieldID);
        clearingPublicFieldIDsRef.current = [...clearingPublicFieldIDsRef.current, publicValueFieldID];
        await setFieldValue(publicValueFieldID, null);
        handleUpdatedFieldIDs(publicValueFieldID);
        handleCurrentUpdatingFieldID(publicValueFieldID);
        await handleSubmit();
        setActivePublicFieldIDs((currentFieldIDs) =>
            currentFieldIDs.filter((currentFieldID) => currentFieldID !== publicValueFieldID)
        );
    };

    const saveFeatures = async (values, setSubmitting) => {
        const Items = [];
        const currentUpdatingFieldID = updatingFieldIDRef.current || updatingFieldID;
        if (currentUpdatingFieldID) {
            const realValueFieldID = getRealValueFieldID(currentUpdatingFieldID);
            const publicValueFieldID = getPublicValueFieldID(realValueFieldID);
            const groupFeature = featuresGroup.GroupFeatures.find(
                (groupFeature) => String(groupFeature.ID) === String(realValueFieldID)
            );

            if (groupFeature) {
                const isClearingPublicValue = clearingPublicFieldIDsRef.current.find(
                    (fieldID) => fieldID === publicValueFieldID
                );
                const item = {
                    FeatureID: groupFeature.FeatureID,
                    // test di presenza e non di verità: uno 0 su una feature numerica è un valore
                    Value: hasFeatureValue(values[realValueFieldID]) ? values[realValueFieldID] : '',
                };

                if (activePublicFieldIDs.find((fieldID) => fieldID === publicValueFieldID) || isClearingPublicValue) {
                    item.PublicValue = isClearingPublicValue
                        ? null
                        : hasFeatureValue(values[publicValueFieldID])
                          ? values[publicValueFieldID]
                          : null;
                }

                Items.push(item);
                clearingPublicFieldIDsRef.current = clearingPublicFieldIDsRef.current.filter(
                    (fieldID) => fieldID !== publicValueFieldID
                );
            }
        }
        if (!Items.length) {
            setSubmitting(false);
            return;
        }
        const data = {
            ContactID: contact.ID,
            ContactType: contactType.charAt(0),
            Features: [...Items],
        };
        // la POST resta volutamente non attesa (un campo per volta, senza bloccare il form):
        // il catch serve a non lasciare il fallimento muto, con il valore nuovo ancora a video
        editFeatures({ data, disableRefetchContact: true }).catch((error) => {
            notification.error({
                duration: 0,
                message: 'Error',
                description: (error && error.message) || 'Feature not saved',
                icon: <NwIcon icon={byPrefixAndName.fass['exclamation-circle']} />,
            });
        });
        setSubmitting(false);
    };

    const groupFeatures = useMemo(() => {
        return getVisibleGroupFeatures(featuresGroup, globalFeatures, globalConversions, contact);
    }, [featuresGroup, globalFeatures, globalConversions, contact]);

    return (
        <NwDrawer title="Edit Feature" onClose={onClose}>
            <FormContainer>
                <AreaName>{featuresGroup.Name}</AreaName>
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);
                        saveFeatures(values, setSubmitting);
                    }}
                >
                    {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                        <NwForm onFinish={handleSubmit}>
                            <Row gutter={10}>
                                {groupFeatures.map((el) => {
                                    const publicValueFieldID = getPublicValueFieldID(el.ID);
                                    const isPublicValueActive = activePublicFieldIDs.find(
                                        (fieldID) => fieldID === publicValueFieldID
                                    );
                                    // su una feature booleana il valore pubblico non aggiunge nulla:
                                    // non si può aggiungere, ma resta rimovibile se già salvato
                                    const canAddPublicValue = el.Feature.TypeName !== 'Boolean';
                                    const publicValueItem = getPublicValueItem(el, publicValueFieldID);
                                    // updatedFieldIDs contiene sempre l'ID reale: privato e pubblico
                                    // accendono la stessa label, che è una sola per feature
                                    const isActive = updatedFieldIDs.find((fieldName) => fieldName === el.ID);
                                    return (
                                        <React.Fragment key={el.ID}>
                                            <Col span={getFeatureFormColSpan()}>
                                                <Fieldset>
                                                    <FeatureLabelHeader
                                                        id={
                                                            el.Feature.Name === editFeatureName
                                                                ? 'active-feature'
                                                                : undefined
                                                        }
                                                    >
                                                        <Label $isActive={isActive} htmlFor={el.ID}>
                                                            {el.Feature.Name}
                                                        </Label>
                                                        {!isPublicValueActive && canAddPublicValue && (
                                                            <NwLinkButton
                                                                htmlType="button"
                                                                icon={byPrefixAndName.fass['circle-plus']}
                                                                label="public"
                                                                disabled={isSubmitting}
                                                                onClick={() =>
                                                                    handleActivatePublicValue(
                                                                        el.ID,
                                                                        values[el.ID],
                                                                        setFieldValue
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {isPublicValueActive && (
                                                            <NwLinkButton
                                                                htmlType="button"
                                                                icon={byPrefixAndName.fasl['times']}
                                                                label="clear public"
                                                                disabled={isSubmitting}
                                                                onClick={() =>
                                                                    handleClearPublicValue(
                                                                        el.ID,
                                                                        setFieldValue,
                                                                        handleSubmit
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </FeatureLabelHeader>
                                                    <FeatureFieldsContainer>
                                                        {createFeatureItemForm(
                                                            el,
                                                            values[el.ID],
                                                            globalConversions,
                                                            setFieldValue,
                                                            featuresIndex,
                                                            handleSubmit,
                                                            isSubmitting,
                                                            handleUpdatedFieldIDs,
                                                            handleCurrentUpdatingFieldID,
                                                            getContactGender(contact),
                                                            isPublicValueActive ? 'PRIVATE' : null
                                                        )}
                                                        {isPublicValueActive &&
                                                            createFeatureItemForm(
                                                                publicValueItem,
                                                                values[publicValueFieldID],
                                                                globalConversions,
                                                                setFieldValue,
                                                                featuresIndex,
                                                                handleSubmit,
                                                                isSubmitting,
                                                                handleUpdatedFieldIDs,
                                                                handleCurrentUpdatingFieldID,
                                                                getContactGender(contact),
                                                                'PUBLIC'
                                                            )}
                                                    </FeatureFieldsContainer>
                                                </Fieldset>
                                            </Col>
                                        </React.Fragment>
                                    );
                                })}
                            </Row>
                        </NwForm>
                    )}
                </Formik>
            </FormContainer>
        </NwDrawer>
    );
};

export default EditFeatureDrawer;
